import React, { useEffect, useMemo, useState } from "react";
import { Line, Scatter } from "react-chartjs-2";
import { Alert, Button, Popover, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { COLORS, UI_DATE_FORMAT } from "../../../../consts";
import { useUser } from "../../../../context/UserContext";
import { cmToInches, inchesToCm } from "../../../../utils/cmToInchesConversion";

const graphColors = COLORS.graphColors;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hexToRgb = (hexColor) => {
  const hex = hexColor?.replace("#", "");
  if (!hex || (hex.length !== 3 && hex.length !== 6)) return null;

  const normalized =
    hex.length === 3
      ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
      : hex;

  const parsed = Number.parseInt(normalized, 16);
  if (Number.isNaN(parsed)) return null;

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
};

const toHeatColor = (baseColor, value, maxValue) => {
  const { r, g, b } = hexToRgb(baseColor) || {
    r: 24,
    g: 144,
    b: 255,
  };
  const safeMax = maxValue > 0 ? maxValue : 1;
  const ratio = clamp(value / safeMax, 0, 1);
  const alpha = 0.08 + ratio * 0.92;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const parseBinRange = (label) => {
  const rawLabel = String(label).trim();
  if (rawLabel.includes("-")) {
    const [startRaw, endRaw] = rawLabel.split("-");
    const start = Number(startRaw);
    const end = Number(endRaw);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      const lower = Math.min(start, end);
      const upper = Math.max(start, end);
      return { start: lower, end: upper > lower ? upper : lower + 1 };
    }
  }

  const start = Number(rawLabel);
  if (!Number.isFinite(start)) return null;
  return { start, end: start + 1 };
};

const getQuantileFromDistribution = (binRanges, percentages, quantile) => {
  const safePercentages = percentages.map((value) =>
    Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0,
  );
  const total = safePercentages.reduce((sum, value) => sum + value, 0);
  if (total <= 0 || binRanges.length === 0) return null;

  const target = total * clamp(quantile, 0, 1);
  let cumulative = 0;

  for (let i = 0; i < binRanges.length; i += 1) {
    const binRange = binRanges[i];
    if (!binRange) continue;
    const current = safePercentages[i];
    const next = cumulative + current;
    if (next >= target) {
      if (current <= 0) return binRange.start;
      const ratio = clamp((target - cumulative) / current, 0, 1);
      return binRange.start + (binRange.end - binRange.start) * ratio;
    }
    cumulative = next;
  }

  for (let i = binRanges.length - 1; i >= 0; i -= 1) {
    if (binRanges[i]) return binRanges[i].end;
  }
  return null;
};

const getModeFromDistribution = (binRanges, percentages) => {
  if (!binRanges.length || !percentages.length) return null;
  let maxIndex = 0;
  for (let i = 1; i < percentages.length; i += 1) {
    if ((percentages[i] ?? 0) > (percentages[maxIndex] ?? 0)) {
      maxIndex = i;
    }
  }
  const binRange = binRanges[maxIndex];
  if (!binRange) return null;
  return (binRange.start + binRange.end) / 2;
};

const getQuantileFromValues = (values, quantile) => {
  const numericValues = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (!numericValues.length) return null;

  const q = clamp(quantile, 0, 1);
  const position = (numericValues.length - 1) * q;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);

  if (lowerIndex === upperIndex) return numericValues[lowerIndex];

  const lowerValue = numericValues[lowerIndex];
  const upperValue = numericValues[upperIndex];
  const ratio = position - lowerIndex;
  return lowerValue + (upperValue - lowerValue) * ratio;
};

const getSpecSplitPercentages = (dataset, specBounds, binRanges) => {
  const rawSizes = (dataset.rawSizes || [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (rawSizes.length > 0) {
    const under = rawSizes.filter((value) => value < specBounds.lower).length;
    const inside = rawSizes.filter(
      (value) => value >= specBounds.lower && value <= specBounds.upper,
    ).length;
    const over = rawSizes.filter((value) => value > specBounds.upper).length;
    const total = rawSizes.length;
    return {
      under: (under / total) * 100,
      inside: (inside / total) * 100,
      over: (over / total) * 100,
    };
  }

  const percentages = dataset.data?.slice(0, binRanges.length) || [];
  let under = 0;
  let inside = 0;
  let over = 0;

  percentages.forEach((value, index) => {
    const numericValue = Number.isFinite(Number(value)) ? Number(value) : 0;
    const binRange = binRanges[index];
    if (!binRange) return;
    const center = (binRange.start + binRange.end) / 2;
    if (center < specBounds.lower) under += numericValue;
    else if (center > specBounds.upper) over += numericValue;
    else inside += numericValue;
  });

  const total = under + inside + over;
  if (total <= 0) return { under: 0, inside: 0, over: 0 };

  return {
    under: (under / total) * 100,
    inside: (inside / total) * 100,
    over: (over / total) * 100,
  };
};

function hexToRGBA(hex, alpha = 0.2) {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      // e.g. #123 => #112233
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    const r = parseInt(c[0] + c[1], 16);
    const g = parseInt(c[2] + c[3], 16);
    const b = parseInt(c[4] + c[5], 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return `rgba(0, 0, 0, ${alpha})`;
}

export const DistributionGraph = ({
  BIN_START,
  BIN_END,
  forecastData,
  specData,
  isImperial = false,
  isFullscreen = false,
}) => {
  const [datasets, setDatasets] = useState([]);
  const [lineVisibility, setLineVisibility] = useState({});
  const [graphType, setGraphType] = useState("line");
  const { userData } = useUser();
  const isSuperUser = Boolean(userData?.is_superuser);
  const chartHeightClass = isFullscreen
    ? "h-full min-h-0"
    : "h-[600px] lg:h-[520px] sm:h-[420px]";

  // x-axis bins from BIN_START..(BIN_END - 1)
  const labels = Array.from(
    { length: BIN_END - BIN_START },
    (_, i) => isImperial ? (i + BIN_START) * 0.5 : (i + BIN_START),
  );

  const annotationConfig = useMemo(() => {
    if (!specData?.length) return {};

    const annotationsObj = {};

    specData.forEach((spec, index) => {
      const colorHex = spec.graph_band_color
        ? spec.graph_band_color
        : graphColors[index % graphColors.length];

      const lbRaw = parseFloat(spec[spec.lower_bound_key_name]) || 0;
      const ubRaw = parseFloat(spec[spec.upper_bound_key_name]) || 0;

      // Scale bounds to match bin indexing: multiply by 2 for imperial (0.5 inch scale)
      const scaledLb = isImperial ? lbRaw * 2 : lbRaw;
      const scaledUb = isImperial ? ubRaw * 2 : ubRaw;
      const lower = Math.min(scaledLb, scaledUb);
      const upper = Math.max(scaledLb, scaledUb);

      annotationsObj[`specBand_${spec.label}`] = {
        type: "box",
        xMin: lower,
        xMax: upper,
        yMin: 0,
        yMax: "100%",
        backgroundColor: hexToRGBA(colorHex, 0.2),
        borderWidth: 0.3,
        borderColor: colorHex,
        label: {
          enabled: true,
          content: spec.label,
          position: {
            x: "center", // "start" | "center" | "end" | {number}
            y: "center",
          },
          font: {
            size: 12,
          },
        },
        drawTime: "beforeDatasetsDraw", // so it appears behind the lines
      };
    });

    return annotationsObj;
  }, [specData, isImperial]);

  /**
   * 2) Chart configuration
   */
  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems[0];
            return `Size: ${item.label} ${isImperial ? "in" : "cm"}`;
          },
          // Show the raw value as a percentage
          label: (tooltipItem) => {
            const rawValue = tooltipItem.raw || 0;
            return `${tooltipItem.dataset.label}: ${rawValue.toFixed(2)}%`;
          },
        },
      },
      annotation: {
        annotations: annotationConfig,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
          font: { size: 14 },
        },
        min: BIN_START,
        max: BIN_END,
        grid: { display: false },
        ticks: {
          autoSkip: false,
          font: { size: 14 },
        },
      },
      y: {
        title: {
          display: true,
          text: "Percentage of Broccoli Heads",
          font: { size: 14 },
        },
        grid: { display: false },
        beginAtZero: true,
        ticks: {
          autoSkip: false,
          font: { size: 14 },
        },
      },
    },
  };

  /**
   * 3) Build the line datasets from forecastData
   */
  useEffect(() => {
    if (!forecastData?.length) {
      setDatasets([]);
      setLineVisibility({});
      return;
    }

    const newDatasets = forecastData.map((entry, idx) => {
      const dateStr = entry.forecast_date;
      const rawApiSizes = entry.data?.sizes || [];
      const numericSizes = rawApiSizes
        .map((s) => (s === null || s === "" ? NaN : Number(s)))
        .filter(Number.isFinite);
      const allSizes = isImperial ? numericSizes.map((s) => cmToInches(s)) : numericSizes;

      // binCounts for each integer from BIN_START..(BIN_END - 1)
      const binCounts = Array.from({ length: BIN_END - BIN_START }, () => 0);

      allSizes.forEach((size) => {
        const binIndex = isImperial ? Math.floor(size * 2) - BIN_START : Math.floor(size) - BIN_START;
        if (binIndex >= 0 && binIndex < binCounts.length) {
          binCounts[binIndex] += 1;
        }
      });

      // Convert absolute counts => percentage
      const totalHeads = allSizes.length;
      if (totalHeads > 0) {
        for (let i = 0; i < binCounts.length; i++) {
          binCounts[i] = (binCounts[i] / totalHeads) * 100;
        }
      } else {
        for (let i = 0; i < binCounts.length; i++) {
          binCounts[i] = 0;
        }
      }

      return {
        label: dayjs(dateStr).format(UI_DATE_FORMAT),
        data: binCounts,
        borderColor: graphColors[idx % graphColors.length],
        backgroundColor: graphColors[idx % graphColors.length],
        tension: 0.3,
        hidden: false,
        rawSizes: allSizes
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value)),
      };
    });

    // Initialize lineVisibility for toggling lines
    const initialVisibility = {};
    newDatasets.forEach((ds) => {
      initialVisibility[ds.label] = true;
    });
    setLineVisibility(initialVisibility);
    setDatasets(newDatasets);
  }, [forecastData, BIN_START, BIN_END, isImperial]);

  /**
   * Toggle a line on/off
   */
  const toggleLineVisibility = (label) => {
    setLineVisibility((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  /**
   * Final chart data
   */
  const chartDatasets = datasets.map((ds) => ({
    ...ds,
    type: "line",
    tension: 0.3,
    borderWidth: 2,
    pointRadius: 2,
    pointHoverRadius: 4,
    hidden: !lineVisibility[ds.label],
  }));

  const chartData = {
    labels,
    datasets: chartDatasets,
  };

  const graphTypeOptions = [
    { label: "Line", value: "line" },
    { label: "Heatmap", value: "heatmap" },
    { label: "Quantile Trend", value: "quantile" },
    { label: "Spec Mix (100%)", value: "specMix" },
    { label: "ECDF Trend", value: "ecdf" },
    { label: "Delta Trend", value: "delta" },
    { label: "In-Spec KPI Trend", value: "kpi" },
  ];

  const graphInfoByType = {
    line: {
      short:
        "Line Distribution: Compare how the full size distribution shape shifts by forecast date.",
      details: [
        "Each line is one forecast date.",
        "Y values are per-bin percentages: (bin count / total heads for that date) * 100.",
      ],
      relevance:
        "Best for seeing peak movement, spread, and tail behavior across forecast dates.",
    },
    heatmap: {
      short:
        "Heatmap: Quick density scan across dates and size bins (higher opacity = higher percentage).",
      details: [
        "Rows represent forecast dates; columns represent size bins.",
        "Cell intensity is normalized by that view's max bin percentage.",
      ],
      relevance:
        "Best for quickly spotting where concentration bands move over time.",
    },
    quantile: {
      short:
        "Quantile Trend: Tracks P25, P50 (median), P75, and mode over dates.",
      details: [
        "P25/P50/P75 are computed from raw sizes (sorted + interpolated quantiles).",
        "Mode is estimated from the highest histogram bin midpoint.",
      ],
      relevance:
        "Best for median drift and spread changes without reading every distribution curve.",
    },
    specMix: {
      short:
        "Spec Mix (100%): Shows under-spec, in-spec, and over-spec share by date.",
      details: [
        "Primary calculation uses raw sizes against current configured spec bounds.",
        "Fallback uses histogram-bin center approximation when raw sizes are unavailable.",
      ],
      relevance: "Best for operational quality split tracking over time.",
    },
    ecdf: {
      short: "ECDF Trend: Cumulative distribution by size for each date.",
      details: [
        "For each date: cumulative sum of bin percentages, normalized to 0-100%.",
        "At any x, the curve shows % of heads <= that size.",
      ],
      relevance:
        "Best for percentile reading and direct stochastic dominance comparison between dates.",
    },
    delta: {
      short:
        "Delta Trend: Shows median shift and IQR-width shift relative to first visible date.",
      details: [
        "Median shift = P50(date) - P50(baseline).",
        "IQR width shift = (P75-P25)(date) - (P75-P25)(baseline).",
      ],
      relevance:
        "Best for quantifying movement direction and spread change with a stable baseline.",
    },
    kpi: {
      short: "In-Spec KPI Trend: Single KPI trend for in-spec percentage.",
      details: [
        "In-spec % = count(lower <= size <= upper) / total * 100 from raw sizes.",
        "Uses same configured bounds as the Spec Mix view.",
      ],
      relevance: "Best for quick decision dashboards and target tracking.",
    },
  };
  const activeGraphInfo = graphInfoByType[graphType] || graphInfoByType.line;

  const visibleDatasets = useMemo(
    () => datasets.filter((dataset) => lineVisibility[dataset.label] !== false),
    [datasets, lineVisibility],
  );

  const rowColorByLabel = useMemo(
    () =>
      visibleDatasets.reduce((acc, dataset, index) => {
        acc[dataset.label] =
          dataset.borderColor ||
          dataset.backgroundColor ||
          graphColors[index % graphColors.length];
        return acc;
      }, {}),
    [visibleDatasets],
  );

  const heatmapMaxValue = useMemo(() => {
    let maxValue = 0;
    visibleDatasets.forEach((dataset) => {
      dataset.data?.forEach((value) => {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
          maxValue = Math.max(maxValue, numericValue);
        }
      });
    });
    return maxValue;
  }, [visibleDatasets]);

  const heatmapData = useMemo(
    () =>
      visibleDatasets.flatMap((dataset) =>
        dataset.data?.map((value, index) => ({
          x: labels[index],
          y: dataset.label,
          v: Number.isFinite(Number(value)) ? Number(value) : 0,
        })),
      ),
    [labels, visibleDatasets],
  );

  const heatmapOptions = useMemo(() => {
    return {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: () => "Distribution Density",
            label: (tooltipItem) => {
              const { x, y, v } = tooltipItem.raw || {};
              const unit = isImperial ? "in" : "cm";
              return `${y} | ${x} ${unit}: ${(v || 0).toFixed(2)}%`;
            },
          },
        },
        annotation: {
          annotations: annotationConfig,
        },
      },
      scales: {
        x: {
          type: "category",
          labels,
          title: {
            display: true,
            text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
            font: { size: 14 },
          },
          min: BIN_START,
          max: BIN_END,
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 14 },
          },
        },
        y: {
          type: "category",
          labels: visibleDatasets.map((dataset) => dataset.label),
          reverse: true,
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 14 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
      },
      datasets: {
        scatter: {
          showLine: false,
          pointStyle: "rectRounded",
          borderWidth: 0,
          pointRadius: (context) => {
            const chartArea = context.chart?.chartArea;
            if (!chartArea) return 6;
            const xCount = Math.max(labels.length, 1);
            const yCount = Math.max(visibleDatasets.length, 1);
            const cellWidth = chartArea.width / xCount;
            const cellHeight = chartArea.height / yCount;
            return Math.max(2, Math.min(cellWidth, cellHeight) / 2 - 1);
          },
          pointHoverRadius: 8,
          backgroundColor: (context) => {
            const value = Number(context.raw?.v) || 0;
            const rowLabel = context.raw?.y;
            const rowColor = rowColorByLabel[rowLabel] || COLORS.primaryBlue;
            return toHeatColor(rowColor, value, heatmapMaxValue);
          },
        },
      },
    };
  }, [
    BIN_END,
    BIN_START,
    annotationConfig,
    heatmapMaxValue,
    isImperial,
    labels,
    rowColorByLabel,
    visibleDatasets,
  ]);

  const heatmapChartData = useMemo(
    () => ({
      datasets: [
        {
          label: "Distribution Density",
          data: heatmapData,
        },
      ],
    }),
    [heatmapData],
  );

  const binRanges = useMemo(() => labels.map((label) => parseBinRange(label)), [labels]);

  const quantileChartData = useMemo(() => {
    const dateLabels = visibleDatasets.map((dataset) => dataset.label);
    const p25 = [];
    const p50 = [];
    const p75 = [];
    const mode = [];

    visibleDatasets.forEach((dataset) => {
      const percentages = dataset.data?.slice(0, binRanges.length) || [];
      const rawSizes = dataset.rawSizes || [];
      const q25 = getQuantileFromValues(rawSizes, 0.25);
      const q50 = getQuantileFromValues(rawSizes, 0.5);
      const q75 = getQuantileFromValues(rawSizes, 0.75);

      p25.push(
        q25 !== null
          ? q25
          : getQuantileFromDistribution(binRanges, percentages, 0.25),
      );
      p50.push(
        q50 !== null
          ? q50
          : getQuantileFromDistribution(binRanges, percentages, 0.5),
      );
      p75.push(
        q75 !== null
          ? q75
          : getQuantileFromDistribution(binRanges, percentages, 0.75),
      );
      mode.push(getModeFromDistribution(binRanges, percentages));
    });

    return {
      labels: dateLabels,
      datasets: [
        {
          label: "P25",
          data: p25,
          borderColor: "rgba(59, 130, 246, 0.4)",
          backgroundColor: "rgba(59, 130, 246, 0.10)",
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
        {
          label: "P75",
          data: p75,
          borderColor: "rgba(59, 130, 246, 0.4)",
          backgroundColor: "rgba(59, 130, 246, 0.10)",
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: "-1",
        },
        {
          label: "P50 (Median)",
          data: p50,
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          borderWidth: 2.5,
          pointRadius: 3,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
        {
          label: "Mode",
          data: mode,
          borderColor: "#22c55e",
          backgroundColor: "#22c55e",
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
      ],
    };
  }, [binRanges, visibleDatasets]);

  const quantileOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)} ${isImperial ? "in" : "cm"}`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 14 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
        y: {
          title: {
            display: true,
            text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
            font: { size: 14 },
          },
          grid: { display: false },
          beginAtZero: true,
          ticks: {
            font: { size: 14 },
          },
        },
      },
    }),
    [isImperial],
  );

  const specBounds = useMemo(() => {
    const firstSpec = specData?.[0];
    if (!firstSpec) {
      return { lower: BIN_START, upper: BIN_END };
    }
    const rawLower = Number(firstSpec[firstSpec.lower_bound_key_name] ?? BIN_START);
    const rawUpper = Number(firstSpec[firstSpec.upper_bound_key_name] ?? BIN_END);
    const safeLower = Number.isFinite(rawLower) ? rawLower : BIN_START;
    const safeUpper = Number.isFinite(rawUpper) ? rawUpper : BIN_END;
    // Convert bounds to cm for filtering
    // Imperial: bounds are in inches → cm (×2.54)
    // Metric: already in cm
    const convertedLower = isImperial ? inchesToCm(safeLower) : safeLower;
    const convertedUpper = isImperial ? inchesToCm(safeUpper) : safeUpper;
    return {
      lower: Math.min(convertedLower, convertedUpper),
      upper: Math.max(convertedLower, convertedUpper),
    };
  }, [BIN_END, BIN_START, isImperial, specData]);

  const specMixChartData = useMemo(() => {
    const dateLabels = visibleDatasets.map((dataset) => dataset.label);
    const underSpec = [];
    const inSpec = [];
    const overSpec = [];

    visibleDatasets.forEach((dataset) => {
      const split = getSpecSplitPercentages(dataset, specBounds, binRanges);
      underSpec.push(split.under);
      inSpec.push(split.inside);
      overSpec.push(split.over);
    });

    return {
      labels: dateLabels,
      datasets: [
        {
          label: "Under Spec",
          data: underSpec,
          borderColor: "#f97316",
          backgroundColor: "rgba(249, 115, 22, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
        {
          label: "In Spec",
          data: inSpec,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
        {
          label: "Over Spec",
          data: overSpec,
          borderColor: "#6366f1",
          backgroundColor: "rgba(99, 102, 241, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
      ],
    };
  }, [binRanges, specBounds, visibleDatasets]);

  const specMixOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 14 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
        y: {
          title: {
            display: true,
            text: "Share (%)",
            font: { size: 14 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          stacked: true,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [],
  );

  const ecdfChartData = useMemo(
    () => ({
      labels,
      datasets: datasets.map((dataset, index) => {
        const percentages = dataset.data?.slice(0, binRanges.length) || [];
        const safePercentages = percentages.map((value) =>
          Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0,
        );
        const total = safePercentages.reduce((sum, value) => sum + value, 0);
        let cumulative = 0;
        const cdf = safePercentages.map((value) => {
          cumulative += value;
          return total > 0 ? (cumulative / total) * 100 : 0;
        });

        return {
          label: dataset.label,
          data: cdf,
          borderColor:
            dataset.borderColor ||
            dataset.backgroundColor ||
            graphColors[index % graphColors.length],
          backgroundColor:
            dataset.backgroundColor ||
            dataset.borderColor ||
            graphColors[index % graphColors.length],
          tension: 0.25,
          borderWidth: 2,
          pointRadius: 1.5,
          pointHoverRadius: 4,
          hidden: !lineVisibility[dataset.label],
          fill: false,
          spanGaps: true,
        };
      }),
    }),
    [binRanges.length, datasets, labels, lineVisibility],
  );

  const ecdfOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
            font: { size: 14 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 14 } },
        },
        y: {
          title: {
            display: true,
            text: "Cumulative Share (%)",
            font: { size: 14 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [isImperial],
  );

  const deltaChartData = useMemo(() => {
    const dateLabels = visibleDatasets.map((dataset) => dataset.label);
    const metrics = visibleDatasets.map((dataset) => {
      const percentages = dataset.data?.slice(0, binRanges.length) || [];
      const rawSizes = dataset.rawSizes || [];
      const q25 =
        getQuantileFromValues(rawSizes, 0.25) ??
        getQuantileFromDistribution(binRanges, percentages, 0.25);
      const q50 =
        getQuantileFromValues(rawSizes, 0.5) ??
        getQuantileFromDistribution(binRanges, percentages, 0.5);
      const q75 =
        getQuantileFromValues(rawSizes, 0.75) ??
        getQuantileFromDistribution(binRanges, percentages, 0.75);
      return {
        median: Number.isFinite(q50) ? q50 : null,
        iqr:
          Number.isFinite(q25) && Number.isFinite(q75) ? q75 - q25 : null,
      };
    });

    const baseline = metrics.find(
      (item) => Number.isFinite(item.median) && Number.isFinite(item.iqr),
    );
    const baselineMedian = baseline?.median ?? 0;
    const baselineIqr = baseline?.iqr ?? 0;

    return {
      labels: dateLabels,
      datasets: [
        {
          label: isImperial ? "Median Shift (in)" : "Median Shift (cm)",
          data: metrics.map((item) =>
            Number.isFinite(item.median) ? item.median - baselineMedian : null,
          ),
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          borderWidth: 2.5,
          pointRadius: 2.5,
          tension: 0.3,
          spanGaps: true,
          fill: false,
        },
        {
          label: isImperial ? "IQR Width Shift (in)" : "IQR Width Shift (cm)",
          data: metrics.map((item) =>
            Number.isFinite(item.iqr) ? item.iqr - baselineIqr : null,
          ),
          borderColor: "#22c55e",
          backgroundColor: "#22c55e",
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 2,
          tension: 0.3,
          spanGaps: true,
          fill: false,
        },
        {
          label: "Baseline",
          data: dateLabels.map(() => 0),
          borderColor: "rgba(107, 114, 128, 0.55)",
          backgroundColor: "rgba(107, 114, 128, 0.55)",
          borderDash: [3, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0,
          fill: false,
        },
      ],
    };
  }, [binRanges, isImperial, visibleDatasets]);

  const deltaOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)} ${isImperial ? "in" : "cm"}`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 14 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 12 } },
        },
        y: {
          title: {
            display: true,
            text: isImperial ? "Change From Baseline (in)" : "Change From Baseline (cm)",
            font: { size: 14 },
          },
          grid: { display: false },
          ticks: { font: { size: 14 } },
        },
      },
    }),
    [isImperial],
  );

  const kpiChartData = useMemo(() => {
    const dateLabels = visibleDatasets.map((dataset) => dataset.label);
    const inSpecSeries = visibleDatasets.map(
      (dataset) => getSpecSplitPercentages(dataset, specBounds, binRanges).inside,
    );

    return {
      labels: dateLabels,
      datasets: [
        {
          label: "In Spec (%)",
          data: inSpecSeries,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderWidth: 2.5,
          pointRadius: 2.5,
          tension: 0.3,
          fill: true,
          spanGaps: true,
        },
      ],
    };
  }, [binRanges, specBounds, visibleDatasets]);

  const kpiOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 14 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 12 } },
        },
        y: {
          title: {
            display: true,
            text: "In Spec Share (%)",
            font: { size: 14 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [],
  );

  return (
    <div
      className={`flex w-full flex-col items-center ${isFullscreen ? "h-full min-h-0 pt-1" : "pt-2"}`}
    >
      {/* Title */}
      <h2
        className={`text-center font-bold ${isFullscreen ? "mb-2 text-xl sm:text-lg" : "mb-4 text-2xl sm:text-xl"}`}
      >
        Head Size Distribution
      </h2>
      {isSuperUser && (
        <div
          className={`flex w-full items-center justify-end ${isFullscreen ? "mb-1" : "mb-2"}`}
        >
          <span
            className="mr-2 text-xs font-semibold"
            style={{ color: COLORS.darkerGray }}
          >
            Graph Type:
          </span>
          <Select
            value={graphType}
            onChange={setGraphType}
            options={graphTypeOptions}
            className="w-[210px]"
          />
        </div>
      )}
      {isSuperUser && (
        <Alert
          type="info"
          showIcon={false}
          className={`${isFullscreen ? "mb-2" : "mb-3"} w-full !py-1`}
          style={{ border: "none", backgroundColor: "transparent" }}
          message={
            <div className="text-xs leading-5" style={{ color: COLORS.primaryBlue }}>
              <span className="font-semibold">{activeGraphInfo.short}</span>{" "}
              <span>{activeGraphInfo.relevance}</span>
              <Popover
                trigger="click"
                placement="bottomRight"
                title="How this graph is calculated"
                content={
                  <div className="max-w-[380px] text-xs leading-5">
                    <div className="mb-1 font-semibold">{activeGraphInfo.relevance}</div>
                    {activeGraphInfo.details.map((detail) => (
                      <div key={detail}>{detail}</div>
                    ))}
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Graph calculation details"
                  icon={<InfoCircleOutlined style={{ color: COLORS.primaryBlue }} />}
                  className="!ml-1 !h-auto !w-auto !min-w-0 !p-0 align-middle"
                />
              </Popover>
            </div>
          }
        />
      )}
      {/* Custom legend for toggling lines (the date lines) */}
      {isFullscreen ? (
        <div className="mb-2 w-full px-2">
          <div className="mx-auto flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {datasets.map((ds, idx) => {
              const color = ds.borderColor || graphColors[idx % graphColors.length];
              const isVisible = lineVisibility[ds.label];
              return (
                <div
                  key={ds.label}
                  className="flex cursor-pointer items-center"
                  onClick={() => toggleLineVisibility(ds.label)}
                >
                  <div
                    className="relative mr-2 h-0.5 w-10"
                    style={{
                      backgroundColor: color,
                      opacity: isVisible ? 1 : 0.3,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: color,
                        opacity: isVisible ? 1 : 0.3,
                      }}
                      className="absolute left-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 transform rounded-full"
                    />
                  </div>
                  <span
                    className="whitespace-nowrap text-xs"
                    style={{ opacity: isVisible ? 1 : 0.3 }}
                  >
                    {ds.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center justify-center">
          {datasets.map((ds, idx) => {
            const color = ds.borderColor || graphColors[idx % graphColors.length];
            const isVisible = lineVisibility[ds.label];
            return (
              <div
                key={ds.label}
                className="mb-2 mr-4 flex cursor-pointer items-center"
                onClick={() => toggleLineVisibility(ds.label)}
              >
                <div
                  className="relative mr-2 h-0.5 w-10"
                  style={{
                    backgroundColor: color,
                    opacity: isVisible ? 1 : 0.3,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: color,
                      opacity: isVisible ? 1 : 0.3,
                    }}
                    className="absolute left-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 transform rounded-full"
                  />
                </div>
                <span
                  className="whitespace-nowrap text-sm sm:text-xs"
                  style={{ opacity: isVisible ? 1 : 0.3 }}
                >
                  {ds.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* "Spec Legend" showing band color and label */}
      {specData?.length > 0 && (
        isFullscreen ? (
          <div className="mb-2 w-full px-2">
            <div className="mx-auto flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              {specData.map((spec, i) => {
                const colorHex = spec.graph_band_color
                  ? spec.graph_band_color
                  : graphColors[i % graphColors.length];
                return (
                  <div key={spec.label} className="flex items-center">
                    <div
                      style={{
                        width: 24,
                        height: 10,
                        backgroundColor: hexToRGBA(colorHex, 0.2),
                        border: `0.5px solid ${colorHex}`,
                        marginRight: 6,
                      }}
                    />
                    <span className="text-xs">{spec.label} Bound</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="mb-4 flex flex-wrap items-center justify-center">
            {specData.map((spec, i) => {
              const colorHex = spec.graph_band_color
                ? spec.graph_band_color
                : graphColors[i % graphColors.length];
              return (
                <div key={spec.label} className="mb-2 mr-4 flex items-center">
                  <div
                    style={{
                      width: 30,
                      height: 12,
                      backgroundColor: hexToRGBA(colorHex, 0.2),
                      border: `0.5px solid ${colorHex}`,
                      marginRight: 6,
                    }}
                  />
                  <span className="text-sm sm:text-xs">{spec.label} Bound</span>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* The chart */}
      <div
        className={`relative w-full ${isFullscreen ? "min-h-0 flex-1 overflow-x-hidden sm:overflow-x-auto p-2 sm:p-1" : "max-h-[800px] overflow-x-auto p-4 sm:p-2"}`}
      >
        <div
          className={`${isFullscreen ? "h-full w-full min-w-0 sm:min-w-[680px]" : `min-w-[640px] ${chartHeightClass}`}`}
        >
          {graphType === "heatmap" ? (
            <Scatter data={heatmapChartData} options={heatmapOptions} />
          ) : graphType === "quantile" ? (
            <Line data={quantileChartData} options={quantileOptions} />
          ) : graphType === "specMix" ? (
            <Line data={specMixChartData} options={specMixOptions} />
          ) : graphType === "ecdf" ? (
            <Line data={ecdfChartData} options={ecdfOptions} />
          ) : graphType === "delta" ? (
            <Line data={deltaChartData} options={deltaOptions} />
          ) : graphType === "kpi" ? (
            <Line data={kpiChartData} options={kpiOptions} />
          ) : (
            <Line data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};
